import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { mh } from "@/lib/mh";
import { Panel, Eyebrow, Stat } from "@/components/mh/bits";
import { CommandCenter } from "@/components/mh/CommandCenter";
import { RolloutMatrix } from "@/components/mh/RolloutMatrix";
import { AutonomyDial } from "@/components/mh/AutonomyDial";
import { AgentRow } from "@/components/mh/AgentRow";
import { FlowRun } from "@/components/mh/FlowRun";
import { ApprovalCard } from "@/components/mh/ApprovalCard";
import { useLiveRuns, useApprovals } from "@/components/mh/hooks";

const TAB = "rounded-full px-4 h-8 text-xs data-[state=active]:bg-white data-[state=active]:text-black";

const LiveFlow = ({ slug, dep }) => {
  const runs = useLiveRuns(slug, 20);
  const { items, reload } = useApprovals(slug);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
      <Panel cls="p-5" testId="dept-live-flow">
        <Eyebrow>Trigger → Retrieve → Reason → Act → Explain → Handoff</Eyebrow>
        <div className="mt-2">
          {runs.length === 0 && <p className="text-sm text-muted-foreground py-6">No runs yet. Put an agent live to see its flow.</p>}
          {runs.map((r) => <FlowRun key={r.id} run={r} deptHex={dep.hex} />)}
        </div>
      </Panel>
      <div className="space-y-3" data-testid="dept-approvals">
        <Eyebrow>Waiting on a human · {items.length}</Eyebrow>
        {items.map((a) => <ApprovalCard key={a.id} item={a} dept={dep} onDone={reload} />)}
      </div>
    </div>
  );
};

export default function DepartmentDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const load = useCallback(() => mh.department(slug).then((r) => setData(r.data)).catch(() => setData(false)), [slug]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <p className="text-muted-foreground" data-testid="dept-not-found">Department not found.</p>;
  if (!data) return <div className="h-[50vh] grid place-items-center font-mono text-xs text-muted-foreground">Loading department…</div>;
  const d = data.department;

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid={`department-page-${slug}`}>
      <Link to="/departments" className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-departments"><ArrowLeft className="w-3.5 h-3.5" /> Departments</Link>
      <header className="relative mh-rise">
        <div className="absolute -top-20 -left-10 w-72 h-72 rounded-full blur-3xl opacity-[0.12] pointer-events-none" style={{ background: d.hex }} />
        <Eyebrow>{String(d.order).padStart(2, "0")} · {d.tagline}</Eyebrow>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-2" style={{ color: d.hex }} data-testid="dept-title">{d.name}</h1>
        <p className="text-base sm:text-lg text-muted-foreground mt-3 max-w-3xl italic font-display">“{d.manifesto}”</p>
      </header>

      <Panel cls="p-5 sm:p-6 grid grid-cols-2 md:grid-cols-4 gap-6 items-end">
        <Stat label="Live" value={`${d.live}/${d.total}`} accent={d.hex} testId="dept-stat-live" />
        <Stat label="Runs today" value={d.runs_today.toLocaleString()} testId="dept-stat-runs" />
        <Stat label="Success" value={`${d.success_rate}%`} testId="dept-stat-success" />
        <div className="col-span-2 md:col-span-1 min-w-0"><Eyebrow cls="mb-2">Autonomy cap</Eyebrow><AutonomyDial slug={slug} value={d.autonomy_cap} hex={d.hex} onChange={load} /></div>
      </Panel>

      <Tabs defaultValue="centers">
        <TabsList className="bg-white/[0.04] border border-white/10 rounded-full p-1 h-auto flex-wrap justify-start">
          <TabsTrigger value="centers" className={TAB} data-testid="tab-centers">Command centers</TabsTrigger>
          <TabsTrigger value="flow" className={TAB} data-testid="tab-flow">Live flow</TabsTrigger>
          <TabsTrigger value="matrix" className={TAB} data-testid="tab-matrix">Rollout matrix</TabsTrigger>
          <TabsTrigger value="agents" className={TAB} data-testid="tab-agents">Agents · {data.agents.length}</TabsTrigger>
        </TabsList>
        <TabsContent value="centers" className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-4">
          {data.dashboards.map((b) => <CommandCenter key={b.slug} board={b} hex={d.hex} />)}
        </TabsContent>
        <TabsContent value="flow" className="mt-5"><LiveFlow slug={slug} dep={d} /></TabsContent>
        <TabsContent value="matrix" className="mt-5"><RolloutMatrix matrix={data.matrix} agents={data.agents} autonomyLevels={data.autonomy_levels} hex={d.hex} /></TabsContent>
        <TabsContent value="agents" className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.agents.map((a, i) => <AgentRow key={a.slug} a={a} i={i} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
